import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Twitter, Youtube, Linkedin, Facebook, Users, Shield, Mail, Phone, MapPin } from 'lucide-react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL;

export const Footer = () => {
  const [settings, setSettings] = useState({
    social_media: {
      instagram: '',
      facebook: '',
      youtube: '',
      twitter: '',
      linkedin: ''
    },
    contact_info: {
      primary_email: '',
      primary_phone: '',
      address_line1: '',
      city: '',
      state: ''
    }
  });

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await axios.get(`${API}/api/settings/all`);
        setSettings(res.data);
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    };
    loadSettings();
  }, []);

  const { social_media, contact_info } = settings;

  // Only show social icons that have links
  const socialLinks = [
    { url: social_media.instagram, icon: Instagram, gradient: 'from-purple-500 to-pink-500', name: 'Instagram' },
    { url: social_media.facebook, icon: Facebook, gradient: 'from-blue-600 to-blue-500', name: 'Facebook' },
    { url: social_media.youtube, icon: Youtube, gradient: 'from-red-500 to-orange-500', name: 'YouTube' },
    { url: social_media.twitter, icon: Twitter, gradient: 'from-blue-500 to-cyan-500', name: 'Twitter' },
    { url: social_media.linkedin, icon: Linkedin, gradient: 'from-blue-700 to-blue-600', name: 'LinkedIn' }
  ].filter(link => link.url);

  return (
    <footer className="bg-muted/50 border-t border-border">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Brand */}
          <div className="col-span-1 md:col-span-2">
            <Link to="/" className="flex items-center gap-3 mb-4">
              <img
                src="https://customer-assets.emergentagent.com/job_via-platform/artifacts/epc7ik46_VIA_LOGO_Tranparent.png"
                alt="VIA Logo"
                className="w-16 h-16 object-contain logo-transparent"
              />
              <span className="font-bold text-xl tracking-tight">Visual Institute of Arts</span>
            </Link>
            <p className="text-muted-foreground max-w-md leading-relaxed">
              Empowering creative minds through world-class art and design education. 
              From Design Foundation to AI Filmmaking - we prepare you for the future.
            </p>
            
            {/* Social Media Icons */}
            {socialLinks.length > 0 && (
              <div className="flex gap-4 mt-6">
                {socialLinks.map((social, index) => (
                  <a 
                    key={index}
                    href={social.url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    title={social.name}
                    className={`w-10 h-10 rounded-full bg-secondary flex items-center justify-center hover:bg-gradient-to-r hover:${social.gradient} hover:text-white transition-all`}
                  >
                    <social.icon className="w-5 h-5" />
                  </a>
                ))}
              </div>
            )}
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2">
              <li><Link to="/programs" className="text-muted-foreground hover:text-primary transition-colors">Programs</Link></li>
              <li><Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">About Us</Link></li>
              <li><Link to="/faculty" className="text-muted-foreground hover:text-primary transition-colors">Faculty</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
            </ul>
          </div>

          {/* For Mentors & Contact */}
          <div>
            <h4 className="font-bold text-lg mb-4">Join Us</h4>
            <ul className="space-y-2">
              <li>
                <Link 
                  to="/mentor/register" 
                  className="flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors"
                >
                  <Users className="w-4 h-4" />
                  Become a Mentor
                </Link>
              </li>
              <li><Link to="/apply" className="text-muted-foreground hover:text-primary transition-colors">Student Registration</Link></li>
              <li><Link to="/login" className="text-muted-foreground hover:text-primary transition-colors">Login</Link></li>
            </ul>
            
            <h4 className="font-bold text-lg mt-6 mb-4">Contact</h4>
            <ul className="space-y-2 text-muted-foreground text-sm">
              {contact_info.primary_email && (
                <li className="flex items-center gap-2">
                  <Mail className="w-4 h-4" />
                  <a href={`mailto:${contact_info.primary_email}`} className="hover:text-primary">
                    {contact_info.primary_email}
                  </a>
                </li>
              )}
              {contact_info.primary_phone && (
                <li className="flex items-center gap-2">
                  <Phone className="w-4 h-4" />
                  <a href={`tel:${contact_info.primary_phone.replace(/\s/g, '')}`} className="hover:text-primary">
                    {contact_info.primary_phone}
                  </a>
                </li>
              )}
              {(contact_info.address_line1 || contact_info.city) && (
                <li className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5" />
                  <span>
                    {contact_info.address_line1 && <>{contact_info.address_line1}<br /></>}
                    {[contact_info.city, contact_info.state].filter(Boolean).join(', ')}
                  </span>
                </li>
              )}
            </ul>
          </div>
        </div>

        <div className="border-t border-border mt-12 pt-8 flex flex-col sm:flex-row justify-between items-center gap-4">
          <p className="text-sm text-muted-foreground">
            © {new Date().getFullYear()} Visual Institute of Arts. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-sm text-muted-foreground">
            <Link to="/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:text-primary transition-colors">Terms of Service</Link>
            <Link to="/refund-policy" className="hover:text-primary transition-colors">Refund Policy</Link>
            <Link 
              to="/admin/login" 
              className="flex items-center gap-1 hover:text-primary transition-colors"
            >
              <Shield className="w-3 h-3" />
              Admin
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
